// Connected container — renders `EntityRow` children which each fetch on
// demand. Not storyable (transitively needs QueryClient + Router + overlay
// context).

import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EntityGroup } from '@/features/document-overlay/grouping'
import { EntityChip } from './EntityChip'
import { EntityRow } from './EntityRow'

export function EntityAccordion({
  caseId,
  documentId,
  group,
  defaultOpen = false,
}: {
  caseId: string
  documentId: string
  group: EntityGroup
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const { t } = useTranslation()
  const notShown = group.total - group.entities.length
  return (
    <div
      style={{
        borderRadius: '12px',
        border: `1px solid ${open ? 'var(--primary)' : 'var(--border)'}`,
        background: open ? 'var(--accent)' : 'var(--card)',
        overflow: 'hidden',
        transition: 'border-color 0.16s ease, background 0.16s ease',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          color: 'var(--foreground)',
          fontFamily: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <ChevronDown
          size={13}
          style={{
            color: 'var(--muted-foreground)',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.18s ease',
            flexShrink: 0,
          }}
        />
        <EntityChip label={group.label} />
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 12,
            color: 'var(--foreground)',
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {group.total}
        </span>
      </button>
      {open && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {group.entities.map(e => (
            <EntityRow key={e.id} caseId={caseId} documentId={documentId} entity={e} />
          ))}
          {notShown > 0 && (
            <div
              style={{
                padding: '8px 12px',
                fontSize: 11,
                color: 'var(--muted-foreground)',
                fontStyle: 'italic',
              }}
            >
              {t('documents.detail.moreNotShown', { count: notShown })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
